import { useState, useMemo } from "react";
import { Loader2, Check } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useListDestinationPacketTemplates, type DestinationPacketDocument } from "@/lib/api/destinationPackets";
import { DataTable } from "@/components/ui/DataTable/index";
import { type DataTableColumn } from "@/components/ui/DataTable/index";

interface CloneFieldsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTemplate: (templateId: string) => void;
}

function formatDocumentVariant(variant?: string): string {
  if (!variant) return "";
  
  return variant
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export interface CloneTemplateRow extends DestinationPacketDocument {}

export function CloneFieldsModal({ open, onOpenChange, onSelectTemplate }: CloneFieldsModalProps) {
  const listTemplatesResult = useListDestinationPacketTemplates();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [isCloning, setIsCloning] = useState(false);

  const templates = useMemo(() => {
    return listTemplatesResult?.data?.data?.data || [];
  }, [listTemplatesResult]);

  const columns = useMemo((): DataTableColumn<CloneTemplateRow>[] => [
    {
      id: "select",
      header: "",
      size: 40,
      minSize: 40,
      maxSize: 40,
      cell: ({ row }) => {
        const isSelected = selectedTemplateId === row.original.id;
        return (
          <div className="flex items-center justify-center">
            {isSelected && (
              <Check className="h-5 w-5 text-primary" />
            )}
          </div>
        );
      },
      enableSorting: false,
      enableFiltering: false,
    },
    {
      id: "packet_name",
      accessorKey: "destination_template_packet_id",
      header: "Packet Name",
      accessorFn: (row) => row.DestinationTemplatePacket?.name || `Packet ${(row.destination_template_packet_id || "").slice(0, 8)}`,
    },
    {
      id: "template_name",
      accessorKey: "template_name",
      header: "Template",
      cell: ({ row }) => {
        return row.original.DocumentTemplate?.title || formatDocumentVariant(row.original.document_variant);
      },
    },
    {
      id: "document_variant",
      accessorKey: "document_variant",
      header: "Template Type",
      cell: ({ row }) => {
        return formatDocumentVariant(row.original.document_variant);
      },
    },
  ], [selectedTemplateId]);

  const handleRowClick = (row: CloneTemplateRow) => {
    setSelectedTemplateId(row.id);
  };

  const handleClone = async () => {
    if (!selectedTemplateId) return;

    setIsCloning(true);
    try {
      onSelectTemplate(selectedTemplateId);
      onOpenChange(false);
      setSelectedTemplateId(null);
    } finally {
      setIsCloning(false);
    }
  };

  const handleClose = () => {
    if (!isCloning) {
      setSelectedTemplateId(null);
      onOpenChange(false);
    }
  };

  const isLoading = listTemplatesResult.isLoading;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="md:min-w-[60vw] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Clone Fields from Template</DialogTitle>
        </DialogHeader>

        <div className="flex-1 min-h-0 flex flex-col">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading templates...</span>
            </div>
          ) : templates.length === 0 ? (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              No templates available
            </div>
          ) : (
            <div className="flex-1 min-h-0 overflow-hidden">
              <DataTable
                stickyHeaders
                tableClassName="h-[calc(85vh-200px)]"
                data={templates}
                columns={columns}
                enableSorting
                enableFiltering
                enableGrouping
                filterStyle="select"
                filterMode="internal"
                defaultSorting={[{ id: "packet_name", desc: false }]}
                emptyMessage={listTemplatesResult.isFetching ? "Loading" : "No templates found"}
                groupBy={["packet_name"]}
                rowClickable
                onRowClick={handleRowClick}
                loading={isLoading}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isCloning}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleClone}
            disabled={!selectedTemplateId || isCloning}
          >
            {isCloning ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Cloning...
              </>
            ) : (
              "Clone Fields"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

